import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { Box, Avatar, Text, BoxProps } from '@chakra-ui/react'
import {
    MentionsInput,
    Mention,
    SuggestionDataItem,
    OnChangeHandlerFunc,
} from 'react-mentions'

import SharedStack from '~components/shared/wrappers/shared-stack'

/**
 * Markup we serialize mentions as inside the message text. Kept in sync
 * with the server-side `MENTION_REGEX` in
 * `server/src/db/controllers/notification.controller/notification-helpers.ts`.
 */
export const MENTION_MARKUP = '@[__display__](__id__)'

export type MentionUserOption = {
    id: string
    display: string
    avatarUrl?: string | null
}

export interface MentionTextareaRef {
    focus: () => void
    blur: () => void
}

interface Props {
    value: string
    /**
     * Receives the new full mention-marked-up value. Use this directly as
     * `setText` in the parent; do not try to read off a synthetic event.
     */
    onChange: (newValue: string) => void
    placeholder?: string
    isDisabled?: boolean
    autoFocus?: boolean
    minHeight?: number
    /** The full set of teammates that can be `@`-mentioned. */
    mentionUsers: MentionUserOption[]
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
    /** Optional outer Box styling overrides (padding, fontSize, etc.). */
    containerProps?: BoxProps
}

/**
 * Reusable `@`-mention textarea built on `react-mentions`. Renders a
 * Chakra-styled suggestion dropdown with avatars and outputs message text
 * in the canonical mention markup format.
 *
 * Keep visual parity with the project's plain `<Textarea variant="unstyled">`
 * so this can drop into existing input shells without disturbing layout.
 */
const MentionTextarea = forwardRef<MentionTextareaRef, Props>(
    (
        {
            value,
            onChange,
            placeholder,
            isDisabled,
            autoFocus,
            minHeight = 32,
            mentionUsers,
            onKeyDown,
            containerProps,
        },
        ref,
    ) => {
        const inputRef = useRef<HTMLTextAreaElement | null>(null)

        useImperativeHandle(ref, () => ({
            focus: () => inputRef.current?.focus(),
            blur: () => inputRef.current?.blur(),
        }))

        const handleChange: OnChangeHandlerFunc = (
            _event,
            newValue,
        ) => {
            // Re-padding on delete puts the trailing spaces (and the
            // mention) back, so backspace can never remove the tag.
            if (newValue.length < value.length) {
                onChange(newValue)
                return
            }
            onChange(ensureSpaceAfterMentionBlocks(newValue))
        }

        return (
            <Box
                w="full"
                px={3}
                py={3}
                fontSize="sm"
                sx={mentionInputSx(minHeight)}
                {...containerProps}
            >
                <MentionsInput
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    autoFocus={autoFocus}
                    inputRef={inputRef}
                    className="chckup-mention-input"
                    a11ySuggestionsListLabel="Suggested teammates"
                    onKeyDown={onKeyDown}
                    allowSpaceInQuery
                    allowSuggestionsAboveCursor
                    spellCheck={false}
                >
                    <Mention
                        trigger="@"
                        data={mentionUsers}
                        markup={MENTION_MARKUP}
                        displayTransform={(_id, display) => ` @${display} `}
                        appendSpaceOnAdd
                        renderSuggestion={renderUserSuggestion}
                        className="chckup-mention-token"
                    />
                </MentionsInput>
            </Box>
        )
    },
)

MentionTextarea.displayName = 'MentionTextarea'

export default MentionTextarea

/**
 * After a mention is inserted (it sits at the end of the draft), keep a
 * space *outside* the chip so the message reads `[ @name ]  message`.
 * Only pad at the end — rewriting when a letter follows would insert that
 * extra space on the first keystroke instead of with the tag.
 */
function ensureSpaceAfterMentionBlocks(value: string): string {
    return value.replace(/(@\[[^\]]+\]\([A-Za-z0-9_-]+\)) ?$/, '$1  ')
}

function renderUserSuggestion(
    suggestion: SuggestionDataItem,
    _search: string,
    _highlightedDisplay: React.ReactNode,
    _index: number,
    focused: boolean,
) {
    const option = suggestion as MentionUserOption
    return (
        <Box
            px={3}
            py={2}
            bg={focused ? 'gray.100' : 'white'}
            cursor="pointer"
        >
            <SharedStack row align="center" spacing={2}>
                <Avatar
                    size="xs"
                    src={option.avatarUrl || undefined}
                    name={option.display}
                />
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                    {option.display}
                </Text>
            </SharedStack>
        </Box>
    )
}

/**
 * Chakra `sx` block targeting `react-mentions`'s class-based DOM. Tries
 * to make the input look identical to the plain `<Textarea variant="unstyled">`
 * the project uses elsewhere so swapping this in does not jolt the layout.
 *
 * `react-mentions` renders the input as two stacked layers:
 *   - a "highlighter" div underneath that paints the mention chip
 *     **backgrounds** (and emits the matching plain-text characters in
 *     transparent ink so widths line up perfectly), and
 *   - a real `<textarea>` on top that the user actually reads from and
 *     whose text/caret remain visible.
 *
 * Critically, every text node inside the highlighter (including the chip
 * span) MUST be `color: transparent` - if any of them is visible it
 * will ghost behind the textarea text and double-render the word. Only
 * the chip BACKGROUND should be visible; the chip text comes from the
 * textarea sitting on top.
 */
function mentionInputSx(minHeight: number) {
    return {
        '.chckup-mention-input': {
            width: '100%',
            fontSize: 'inherit',
            fontFamily: 'inherit',
            lineHeight: '1.5',
        },
        '.chckup-mention-input__control': {
            minHeight: `${minHeight}px`,
            position: 'relative',
        },
        // Background layer: only the mention-chip background is meant
        // to be visible here. Plain text and chip text both stay
        // transparent so the textarea on top can be the sole source of
        // visible characters.
        '.chckup-mention-input__highlighter': {
            minHeight: `${minHeight}px`,
            padding: 0,
            border: '1px solid transparent',
            color: 'transparent',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflow: 'hidden',
            pointerEvents: 'none',
        },
        // Foreground layer: real <textarea>. Carries the visible text
        // and the caret.
        '.chckup-mention-input__input': {
            minHeight: `${minHeight}px`,
            padding: 0,
            border: '1px solid transparent',
            outline: 'none',
            background: 'transparent',
            resize: 'none',
            width: '100%',
            fontSize: 'inherit',
            fontFamily: 'inherit',
            lineHeight: 'inherit',
            color: 'gray.800',
        },
        // Mention chip - colored background sits behind the textarea
        // text. Text color stays transparent (inherited from the
        // highlighter) so it doesn't double up with the textarea.
        //
        // Background tint is matched to Chakra's `<Tag variant="subtle"
        // colorScheme="brand">`, which is what `MentionText` renders for
        // sent messages, so the chip looks identical in both surfaces.
        //
        // Padding stays at 0 because the underlying `<strong>` inline
        // element would otherwise push subsequent characters in the
        // highlighter and break alignment with the transparent textarea
        // text on top. Horizontal inset comes from spaces inside
        // `displayTransform` (` @name `), so the chip is `[ @name ]`.
        // The box-shadow is vertical only — a full spread would paint
        // into the real space after the mention and make it look like
        // `[ @name  ] message`.
        '.chckup-mention-token': {
            backgroundColor: 'brand.100',
            borderRadius: 'md',
            padding: 0,
            boxShadow:
                '0 -2px 0 0 var(--chakra-colors-brand-100), 0 2px 0 0 var(--chakra-colors-brand-100)',
        },
        // Suggestions popup.
        '.chckup-mention-input__suggestions': {
            backgroundColor: 'white',
            borderRadius: 'md',
            boxShadow: 'lg',
            border: '1px solid',
            borderColor: 'gray.200',
            maxHeight: '240px',
            overflowY: 'auto',
            zIndex: 'popover',
            minWidth: '220px',
        },
        '.chckup-mention-input__suggestions__list': {
            listStyle: 'none',
            margin: 0,
            padding: 0,
        },
        '.chckup-mention-input__suggestions__item': {
            cursor: 'pointer',
        },
    } as const
}
